import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { ClipboardListIcon } from 'lucide-react'

import { ApiError } from '@/api/errors'
import { portalQuestionnairesApi } from '@/api/questionnaires'
import type { PortalQuestionnaire } from '@/api/types'
import { EmptyState } from '@/components/empty-state'
import { FormError } from '@/components/field-error'
import { Frame } from '@/components/frame'
import { PageHeader } from '@/components/page-header'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { msg } from '@/i18n'
import { formatDateOnly, formatDateTime } from '@/lib/format'
import { portalQuestionnaireState, portalSubmittedAt } from '@/lib/questionnaire'

/** Espace client › Questionnaires (lot 23) : ce qu'on vous demande, et ce que vous avez répondu. */
export const Route = createFileRoute('/portal/questionnaires/')({
  component: PortalQuestionnairesPage,
})

const STATE_VARIANTS = {
  todo: 'brand',
  answered: 'success',
  closed: 'muted',
} as const

function PortalQuestionnairesPage() {
  const t = msg().portalForms.questionnaires
  const questionnaires = useQuery({
    queryKey: ['portal', 'questionnaires'],
    queryFn: () => portalQuestionnairesApi.list(),
  })
  // À répondre d'abord ; l'ordre servi ensuite.
  const items = [...(questionnaires.data ?? [])].sort(
    (a, b) =>
      Number(portalQuestionnaireState(b) === 'todo') -
      Number(portalQuestionnaireState(a) === 'todo'),
  )

  return (
    <div className="space-y-6">
      <PageHeader title={t.title} documentTitle={t.title} />
      <Frame>
        {questionnaires.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : questionnaires.isError ? (
          <FormError>
            {questionnaires.error instanceof ApiError ? questionnaires.error.message : t.unreadable}
          </FormError>
        ) : items.length === 0 ? (
          <EmptyState
            icon={ClipboardListIcon}
            title={t.emptyTitle}
            description={t.emptyDescription}
          />
        ) : (
          <ul className="divide-y" data-testid="questionnaires-portail">
            {items.map((questionnaire) => (
              <Row key={questionnaire.id} questionnaire={questionnaire} />
            ))}
          </ul>
        )}
      </Frame>
    </div>
  )
}

function Row({ questionnaire }: { questionnaire: PortalQuestionnaire }) {
  const t = msg().portalForms.questionnaires
  const state = portalQuestionnaireState(questionnaire)
  const submitted = portalSubmittedAt(questionnaire)
  return (
    <li className="space-y-0.5 py-3" data-testid="questionnaire-portail">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          to="/portal/questionnaires/$questionnaireId"
          params={{ questionnaireId: questionnaire.id }}
          className="min-w-0 flex-1 font-medium hover:underline"
        >
          {questionnaire.title}
        </Link>
        <Badge variant={STATE_VARIANTS[state]}>{t.states[state]}</Badge>
      </div>
      <p className="text-xs text-muted-foreground">
        {[
          questionnaire.project?.name,
          t.questions(questionnaire.questions.length),
          submitted
            ? t.sentOn(formatDateTime(submitted))
            : questionnaire.due_date && state === 'todo'
              ? t.dueBy(formatDateOnly(questionnaire.due_date))
              : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </li>
  )
}
